import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Skeleton,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Chip,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAuth } from "@wc/domain/authn";
import {
  PermissionsEnum,
  userHasPermissionInOrg,
  userHasPermissionInTeam,
} from "@wc/domain/authz";
import { formatDate } from "@wc/domain/dates";
import { useOrg } from "@wc/domain/organizations";
import {
  getUserTeamsWithPrimary,
  setPrimaryTeam,
} from "@wc/domain/team-membership";
import { listTeamsByOrg, deleteTeam } from "@wc/domain/teams";
import CreateTeamModal from "@wc/domain/teams/components/create-team-modal";
import { ReactComponent as SettingIcon } from "@wc/ui/assets/icons/setting.svg";

/**
 * Team list component showing all teams in an organization
 * @returns {React.ReactElement} The team list
 */
export default function TeamList() {
  const { t } = useTranslation();

  const authData = useAuth();
  const orgData = useOrg();
  const [teams, setTeams] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [canCreateTeam, setCanCreateTeam] = React.useState(false);
  const [canDeleteOrgLevel, setCanDeleteOrgLevel] = React.useState(false);
  const [teamDeletePermissions, setTeamDeletePermissions] = React.useState({});
  const [teamToDelete, setTeamToDelete] = React.useState(null);
  const [userTeams, setUserTeams] = React.useState([]); // Teams user is a member of with primary status
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onOpenChange: onDeleteModalOpenChange,
  } = useDisclosure();

  const fetchTeams = React.useCallback(async () => {
    if (!orgData?.org?.id || !authData.isLoggedIn()) return;

    try {
      setIsLoading(true);
      const [teamsList, userTeamsList] = await Promise.all([
        listTeamsByOrg(orgData.org.id, authData.getCurrentUser().id),
        getUserTeamsWithPrimary(authData.getCurrentUser().id, orgData.org.id),
      ]);
      setTeams(teamsList);
      setUserTeams(userTeamsList);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    } finally {
      setIsLoading(false);
    }
  }, [authData, orgData?.org?.id]);

  const checkPermissions = React.useCallback(async () => {
    if (!orgData?.org?.id || !authData.isLoggedIn()) return;

    try {
      const hasManageTeams = await userHasPermissionInOrg(
        authData.getCurrentUser(),
        orgData.org.id,
        PermissionsEnum.MANAGE_TEAMS,
      );
      setCanCreateTeam(hasManageTeams);
      setCanDeleteOrgLevel(hasManageTeams);
    } catch {
      setCanCreateTeam(false);
      setCanDeleteOrgLevel(false);
    }
  }, [authData, orgData?.org?.id]);

  // Check team-level delete permissions for each team
  const checkTeamDeletePermissions = React.useCallback(async () => {
    if (!authData.isLoggedIn() || teams.length === 0 || canDeleteOrgLevel)
      return;

    const permissions = {};
    for (const team of teams) {
      try {
        const canDelete = await userHasPermissionInTeam(
          authData.getCurrentUser(),
          team.id,
          PermissionsEnum.DELETE_TEAM,
        );
        permissions[team.id] = canDelete;
      } catch {
        permissions[team.id] = false;
      }
    }
    setTeamDeletePermissions(permissions);
  }, [authData, teams, canDeleteOrgLevel]);

  React.useEffect(() => {
    fetchTeams();
    checkPermissions();
  }, [fetchTeams, checkPermissions]);

  React.useEffect(() => {
    checkTeamDeletePermissions();
  }, [checkTeamDeletePermissions]);

  const handleTeamCreated = () => {
    fetchTeams();
  };

  const canDeleteTeam = (teamId) => {
    return canDeleteOrgLevel || teamDeletePermissions[teamId];
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;

    try {
      setIsLoading(true);
      await deleteTeam(
        teamToDelete.id,
        orgData.org.id,
        authData.getCurrentUser(),
      );
      setTeams(teams.filter((team) => team.id !== teamToDelete.id));
      setTeamToDelete(null);
    } catch (error) {
      console.error("Failed to delete team:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (team) => {
    setTeamToDelete(team);
    onDeleteModalOpen();
  };

  // Check if user is a member of a team
  const isUserMemberOf = (teamId) => {
    return userTeams.some((ut) => ut.id === teamId);
  };

  // Check if a team is the user's primary
  const isPrimaryTeam = (teamId) => {
    const userTeam = userTeams.find((ut) => ut.id === teamId);
    return userTeam?.isPrimary || false;
  };

  // Handle setting a team as primary
  const handleSetPrimary = async (teamId) => {
    try {
      await setPrimaryTeam(
        authData.getCurrentUser().id,
        orgData.org.id,
        teamId,
      );
      // Update local state
      setUserTeams(
        userTeams.map((ut) => ({
          ...ut,
          isPrimary: ut.id === teamId,
        })),
      );
    } catch (error) {
      console.error("Failed to set primary team:", error);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t("team.listTitle")}</h2>
        {canCreateTeam && (
          <Button color="primary" onPress={onOpen}>
            {t("team.createTeam")}
          </Button>
        )}
      </div>

      <Table aria-label="Teams table">
        <TableHeader>
          <TableColumn>Name</TableColumn>
          <TableColumn>{t("team.createdAt")}</TableColumn>
          <TableColumn></TableColumn>
        </TableHeader>
        <TableBody emptyContent={t("team.noTeams")}>
          {isLoading
            ? Array(3)
                .fill()
                .map((_, index) => (
                  <TableRow key={`loading-row-${index}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-3/4 rounded" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-1/2 rounded" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-8 rounded" />
                    </TableCell>
                  </TableRow>
                ))
            : teams.map((team) => {
                const isMember = isUserMemberOf(team.id);
                const isPrimary = isPrimaryTeam(team.id);
                const showDropdown =
                  canDeleteTeam(team.id) || (isMember && !isPrimary);

                return (
                  <TableRow key={team.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/orgs/${orgData.org.id}/teams/${team.id}`}
                          className="text-primary hover:underline"
                        >
                          {team.name}
                        </Link>
                        {isPrimary && (
                          <Chip size="sm" color="primary" variant="flat">
                            {t("team.primary")}
                          </Chip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(team.createdAt)}</TableCell>
                    <TableCell>
                      {showDropdown && (
                        <Dropdown>
                          <DropdownTrigger>
                            <Button isIconOnly variant="light">
                              <SettingIcon
                                className="size-5"
                                role="img"
                                aria-label="settings"
                              />
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu>
                            {isMember && !isPrimary && (
                              <DropdownItem
                                onClick={() => handleSetPrimary(team.id)}
                              >
                                {t("team.setPrimary")}
                              </DropdownItem>
                            )}
                            {canDeleteTeam(team.id) && (
                              <DropdownItem
                                className="text-danger"
                                color="danger"
                                onClick={() => openDeleteModal(team)}
                              >
                                {t("team.deleteTeam")}
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </Dropdown>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>

      {orgData?.org && (
        <CreateTeamModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          organizationId={orgData.org.id}
          onSuccess={handleTeamCreated}
        />
      )}

      <Modal isOpen={isDeleteModalOpen} onOpenChange={onDeleteModalOpenChange}>
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {t("team.deleteModalHeader")}
          </ModalHeader>
          <ModalBody>
            <p>
              {t("team.deleteModalBody", {
                name: teamToDelete?.name,
              })}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onDeleteModalOpenChange}>
              {t("cancel")}
            </Button>
            <Button
              color="danger"
              onPress={() => {
                handleDeleteTeam();
                onDeleteModalOpenChange();
              }}
            >
              {t("team.deleteModalConfirm")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
